import { CheckIcon, Combobox, Group, Loader, Pill, PillsInput, useCombobox } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraFieldData2, JiraUserLiteData } from '../../../../api/endpoints/jira';
import { useResolve } from '../../../Container';

interface MultiUserOptionInputProps {
    field: JiraFieldData2;
    value: JiraUserLiteData[] | null;
    error?: boolean;
    onChange: (value: JiraUserLiteData[] | null) => void;
    onSubmit: (value: JiraUserLiteData[] | null) => void;
}

const useJiraUserQuery = (jiraProjectId: string, query: string) => {
    const api = useResolve(ApiClient);

    const [debouncedQuery] = useDebounce(query, 250);
    return useQuery({
        queryKey: ['jira.projects.users.list', jiraProjectId, debouncedQuery],
        queryFn() {
            return api.exec(v2.jira.projects.users.list(jiraProjectId, debouncedQuery || ''));
        },
    });
};

const useUserSelection = (value: JiraUserLiteData[] | null, onChange: (next: JiraUserLiteData[] | null) => void, onSubmit: (next: JiraUserLiteData[] | null) => void) => {
    const handleUserSelect = useCallback((user: JiraUserLiteData) => {
        const currentValue = value || [];
        const isAlreadySelected = currentValue.some(u => u.id === user.id);

        let newValue: JiraUserLiteData[];
        if (isAlreadySelected) {
            newValue = currentValue.filter(u => u.id !== user.id);
        }
        else {
            newValue = [...currentValue, user];
        }

        const finalValue = newValue.length === 0 ? null : newValue;
        onChange(finalValue);
        onSubmit(finalValue);
    }, [value, onChange, onSubmit]);

    const handleUserRemove = useCallback((user: JiraUserLiteData) => {
        const currentValue = value || [];
        const newValue = currentValue.filter(u => u.id !== user.id);
        const finalValue = newValue.length === 0 ? null : newValue;
        onChange(finalValue);
        onSubmit(finalValue);
    }, [value, onChange, onSubmit]);

    const isUserSelected = useCallback((user: JiraUserLiteData) => {
        return (value || []).some(u => u.id === user.id);
    }, [value]);

    return { handleUserSelect, handleUserRemove, isUserSelected };
};

export const JiraMultiUserOptionInput = React.memo(({ field, value, error, onChange, onSubmit }: MultiUserOptionInputProps) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const [search, setSearch] = useState('');
    const { data, isLoading } = useJiraUserQuery(field.project.id, search);
    const users = data || [];
    const empty = search.length > 0 && users.length === 0 && !isLoading;

    const { handleUserSelect, handleUserRemove, isUserSelected } = useUserSelection(value, onChange, onSubmit);

    const handleSearch = (query: string) => {
        setSearch(query);
        combobox.updateSelectedOptionIndex();
    };

    const handleValueRemove = (user: JiraUserLiteData) => {
        handleUserRemove(user);
    };

    const pills = (value || []).map((user) => (
        <Pill key={user.id} withRemoveButton onRemove={() => handleValueRemove(user)}>
            {user.displayName}
        </Pill>
    ));

    const options = users.map((user) => (
        <Combobox.Option value={user.id} key={user.id} active={isUserSelected(user)}>
            <Group gap="sm">
                {isUserSelected(user) && <CheckIcon size={12} />}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {user.avatarUrl && (
                        <img
                            src={user.avatarUrl}
                            alt={user.displayName}
                            style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                marginRight: 8
                            }}
                        />
                    )}
                    {user.displayName}
                </div>
            </Group>
        </Combobox.Option>
    ));

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={(optionValue) => {
                const user = users.find(u => u.id === optionValue);
                if (user) {
                    handleUserSelect(user);
                }
            }}
            withinPortal={false}
        >
            <Combobox.DropdownTarget>
                <PillsInput
                    onClick={() => combobox.openDropdown()}
                    label={field.name}
                    required={field.required && !field.hasDefaultValue}
                    rightSection={isLoading ? <Loader size={18} /> : null}
                    error={error}
                >
                    <Pill.Group>
                        {pills}
                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                value={search}
                                placeholder={`Search for ${field.name.toLowerCase()}`}
                                onChange={(event) => {
                                    const query = event.currentTarget.value;
                                    setSearch(query);
                                    handleSearch(query);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Backspace' && search.length === 0 && value && value.length > 0) {
                                        event.preventDefault();
                                        handleValueRemove(value[value.length - 1]);
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {options.length > 0 ? options : empty ? <Combobox.Empty>No users found</Combobox.Empty> : null}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
});
